/**
 * Communication Manager - Expert-Tenant Relationship Tracking using FERP Primitives
 *
 * ✅ ARCHITECTURAL COMPLIANCE:
 * - Uses proper FERP 16 primitive types (Context, Task, TimelineItem, Actor)
 * - NO hardcoded types outside the 16 primitives
 * - Data-driven approach with business capability abstraction
 * - Frontend receives business capabilities only (abstracted from backend vendors)
 * - Actor primitive provides role-based feature access
 * - Context primitive manages tenant-specific configurations
 *
 * LIFE-CRITICAL: Maintains audit trails for compliance and billing accuracy.
 */

import { gql, useMutation, useQuery } from '@ferp/graphql-client';
import {
  Alert,
  Badge,
  Button,
  Card,
  Group,
  Modal,
  Stack,
  Table,
  Text,
  TextInput,
  Title,
  ActionIcon,
  Tooltip,
  NumberInput
} from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { notifications } from '@mantine/notifications';
import {
  IconVideo,
  IconMessage,
  IconScreenShare,
  IconPhone,
  IconClock,
  IconCurrencyDollar,
  IconAlertTriangle,
  IconCheck,
  IconSettings,
  IconEye
} from '@tabler/icons-react';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import type { Context, Task, ActorDescriptor } from '../types-16-complete';
import { useGenerateId, EntityType } from '../hooks';
import { IdGenerationRepository } from '../data/repositories/IdGenerationRepository';

// ❌ ARCHITECTURAL FIX: Using 16 primitive types instead of hardcoded interfaces
// CommunicationEvent → TimelineItem primitive
// CommunicationChannel → Context primitive
import type { TimelineItem } from '../types-16-complete';

// Expert communication tracking using proper FERP primitives
interface ExpertCommunicationContext extends Context {
  // Context primitive for communication configuration
  scope: 'tenant';
  configuration: {
    expertId: string; // Actor ID
    integrationId: string;
    communicationType: 'video_call' | 'chat' | 'screen_share' | 'file_transfer';
    billing: {
      hourlyRate: number;
      platformFeePercentage: number;
      complianceLevel: 'standard' | 'hipaa' | 'sox' | 'gdpr';
    };
    enforcement: {
      platformLockIn: boolean;
      recordingRequired: boolean;
      auditTrailEnabled: boolean;
    };
  };
  feature_flags: {
    video_enabled: boolean;
    screen_share_enabled: boolean;
    file_transfer_enabled: boolean;
    recording_enabled: boolean;
  };
}

interface CommunicationSessionTask extends Task {
  // Task primitive for communication sessions
  description: string; // e.g., "Expert consultation session with Jennifer Martinez"
  status: 'OPEN' | 'IN_PROGRESS' | 'BLOCKED' | 'DONE' | 'EXPIRED';
  priority: 'CRITICAL' | 'HIGH' | 'MEDIUM' | 'LOW';
  estimatedEffort: number; // minutes
  owner: string; // Expert Actor ID
  dueDate: Date;
  metadata: {
    sessionType: 'video_call' | 'chat' | 'screen_share' | 'file_transfer';
    participants: string[]; // Actor IDs
    startTime?: string;
    endTime?: string;
    billingAmount?: number;
    satisfactionScore?: number;
    recordingId?: string;
    complianceValidated: boolean;
  };
}

interface CommunicationManagerProps {
  tenant: string;
  expertId?: string;
  integrationId?: string;
  readonly?: boolean;
}

interface CommunicationSession {
  id: string;
  type: 'video_call' | 'chat_thread' | 'screen_share' | 'voice_call';
  expertId: string;
  expertName: string;
  tenantId: string;
  integrationId: string;
  status: 'active' | 'paused' | 'ended' | 'archived';
  startTime: Date;
  endTime?: Date;
  duration: number; // minutes
  billableTime: number; // minutes
  cost: number; // USD
  events: TimelineItem[]; // Using TimelineItem primitive for communication events
  metadata: {
    projectPhase: 'discovery' | 'implementation' | 'testing' | 'deployment' | 'support';
    recordingEnabled: boolean;
    encryptionLevel: 'standard' | 'enterprise' | 'healthcare';
    platformLockIn: boolean;
  };
}

export const CommunicationManager: React.FC<CommunicationManagerProps> = ({
  tenant,
  expertId,
  integrationId,
  readonly = false,
}) => {
  const [channels, setChannels] = useState<Context[]>([]); // Using Context primitive
  const [sessions, setSessions] = useState<CommunicationSession[]>([]);
  const [selectedChannel, setSelectedChannel] = useState<Context | null>(null);
  const [loading, setLoading] = useState(false);
  const [modalOpened, { open: openModal, close: closeModal }] = useDisclosure(false);

  // Session creation state
  const [newSessionType, setNewSessionType] = useState<'video_call' | 'chat_thread' | 'screen_share' | 'voice_call'>('video_call');
  const [sessionNotes, setSessionNotes] = useState('');

  // Backend-generated IDs for communication entities (FERP compliance)
  const { generateId: generateChannelId } = useGenerateId({ entityType: EntityType.CONTEXT });
  const { generateId: generateTaskId } = useGenerateId({ entityType: EntityType.workflowNode });

  // GraphQL queries and mutations for communication channels
  const GET_COMMUNICATION_CHANNELS = gql`
    query GetCommunicationChannels($tenant: String!, $expertId: String, $integrationId: String) {
      communicationChannels(tenant: $tenant, expertId: $expertId, integrationId: $integrationId) {
        id
        type
        expertId
        tenantId
        integrationId
        status
        createdAt
        lastActivity
        billing {
          billableTime
          nonBillableTime
          rates {
            videoCallRate
            chatMessageRate
            screenShareRate
          }
        }
        audit {
          compliance {
            participantConsent
            encryptionVerified
            accessLogged
          }
          activityLog {
            timestamp
            eventType
            participantId
            details
          }
        }
        metadata {
          projectPhase
          participants
          recordingEnabled
          encryptionLevel
          platformLockIn
        }
      }
    }
  `;

  const CREATE_COMMUNICATION_CHANNEL = gql`
    mutation CreateCommunicationChannel($tenant: String!, $channel: CommunicationChannelInput!) {
      createCommunicationChannel(tenant: $tenant, channel: $channel) {
        id
        type
        expertId
        tenantId
        integrationId
        status
        createdAt
        metadata
      }
    }
  `;

  const START_COMMUNICATION_SESSION = gql`
    mutation StartCommunicationSession($tenant: String!, $channelId: ID!, $sessionType: String!, $notes: String) {
      startCommunicationSession(tenant: $tenant, channelId: $channelId, sessionType: $sessionType, notes: $notes) {
        id
        startTime
        status
      }
    }
  `;

  const END_COMMUNICATION_SESSION = gql`
    mutation EndCommunicationSession($tenant: String!, $sessionId: ID!, $duration: Int!) {
      endCommunicationSession(tenant: $tenant, sessionId: $sessionId, duration: $duration) {
        id
        endTime
        billableTime
        cost
      }
    }
  `;

  const { data, refetch } = useQuery<Record<string, any>>(GET_COMMUNICATION_CHANNELS, {
    variables: { tenant, expertId, integrationId }
  } as any);

  const [mutateCreateChannel] = useMutation<Record<string, any>>(CREATE_COMMUNICATION_CHANNEL);
  const [mutateStartSession] = useMutation<Record<string, any>>(START_COMMUNICATION_SESSION);
  const [mutateEndSession] = useMutation<Record<string, any>>(END_COMMUNICATION_SESSION);

  // Sample data for development - would be replaced with real data
  const sampleSessions: CommunicationSession[] = useMemo(() => [
    {
      id: 'session-001',
      type: 'video_call',
      expertId: 'expert-001',
      expertName: 'Jennifer Martinez',
      tenantId: tenant,
      integrationId: integrationId || 'int-001',
      status: 'ended',
      startTime: new Date(Date.now() - 86400000), // 1 day ago
      endTime: new Date(Date.now() - 86400000 + 3600000), // 1 hour duration
      duration: 60,
      billableTime: 55,
      cost: 137.50, // 55 minutes * $150/hour
      events: [
        {
          at_ms: Date.now() - 86400000,
          transition: 'session_start',
          actor: 'expert-001',
          payload: {
            sessionType: 'video_call',
            projectPhase: 'implementation',
            ipAddress: '192.168.1.1',
            deviceInfo: 'Chrome/MacOS'
          }
        },
        {
          at_ms: Date.now() - 86400000 + 3600000,
          transition: 'session_end',
          actor: 'expert-001',
          payload: {
            billableMinutes: 55,
            notes: 'Integration configuration completed', // ✅ ABSTRACTED: No backend-specific references
            ipAddress: '192.168.1.1',
            deviceInfo: 'Chrome/MacOS'
          }
        }
      ],
      metadata: {
        projectPhase: 'implementation',
        recordingEnabled: true,
        encryptionLevel: 'standard',
        platformLockIn: true
      }
    },
    {
      id: 'session-002',
      type: 'chat_thread',
      expertId: 'expert-101',
      expertName: 'Dr. Robert Kim',
      tenantId: tenant,
      integrationId: integrationId || 'int-001',
      status: 'active',
      startTime: new Date(Date.now() - 1800000), // 30 minutes ago
      duration: 30,
      billableTime: 25,
      cost: 104.17, // 25 minutes * $250/hour
      events: [
        {
          at_ms: Date.now() - 1800000,
          transition: 'session_start',
          actor: 'expert-101',
          payload: {
            sessionType: 'chat_thread',
            projectPhase: 'support',
            ipAddress: '10.0.0.1',
            deviceInfo: 'Chrome/Windows'
          }
        }
      ],
      metadata: {
        projectPhase: 'support',
        recordingEnabled: true,
        encryptionLevel: 'enterprise',
        platformLockIn: true
      }
    }
  ], [tenant, integrationId]);

  useEffect(() => {
    // In production, this would come from GraphQL
    setSessions(sampleSessions);
    setChannels(data?.communicationChannels || []);
  }, [data, sampleSessions]);

  // Create a new communication channel for expert-tenant relationship
  const createCommunicationChannel = useCallback(async (expert: ActorDescriptor, integrationId: string) => {
    if (!expert || !integrationId) return;

    setLoading(true);
    try {
      // Generate backend Snowflake ID for chat log (FERP compliance)
      const chatLogId = await generateTaskId();

      const channelInput = {
        type: 'video_call',
        expertId: expert.metadata?.id,
        tenantId: tenant,
        integrationId,
        config: {
          video: {
            maxParticipants: 2,
            recordingRequired: true,
            qualityLevel: 'hd',
            backgroundBlurEnabled: true
          },
          chat: {
            threadingEnabled: true,
            fileUploadLimit: 50,
            retentionDays: 90,
            searchEnabled: true
          },
          screenShare: {
            allowedApplications: ['browser', 'payroll_system', 'excel'], // ✅ ABSTRACTED: No specific vendor names
            annotationEnabled: true,
            recordingRequired: true
          },
          security: {
            endToEndEncryption: true,
            accessControlRequired: true,
            sessionTimeout: 120,
            ipWhitelist: []
          }
        },
        enforcement: {
          contractBinding: {
            requiresSystemCommunication: true,
            externalCommunicationPenalty: 500,
            terminationClause: 'Platform bypass results in contract termination'
          },
          technical: {
            blockExternalEmailDomains: ['gmail.com', 'yahoo.com', 'outlook.com'],
            interceptExternalCommunication: true,
            requireFerpAppUsage: true,
            geofenceEnabled: false
          },
          monitoring: {
            trackCommunicationPatterns: true,
            detectBypassAttempts: true,
            alertOnExternalContact: true,
            complianceReporting: true
          }
        },
        billing: {
          timeTracking: {
            billableTime: 0,
            nonBillableTime: 0,
            overtimeMultiplier: 1.5,
            roundingRules: 'nearest_15min'
          },
          rates: {
            videoCallRate: (expert.metadata?.hourlyRate || 0) / 60, // per minute
            chatMessageRate: 0.35,
            screenShareRate: ((expert.metadata?.hourlyRate || 0) * 1.6) / 60, // premium rate per minute
            fileTransferRate: 0.10
          },
          platformFees: {
            communicationFeePercentage: 20,
            infrastructureFee: 2.50,
            complianceRecordingFee: 1.00
          }
        },
        audit: {
          recordings: {
            chatLogId // Backend-generated Snowflake ID
          },
          compliance: {
            participantConsent: true,
            dataRetentionCompliance: ['SOX', 'GDPR'],
            encryptionVerified: true,
            accessLogged: true
          },
          activityLog: [],
          legal: {
            termsAccepted: true,
            privacyPolicyVersion: 'v2.1',
            jurisdictionAgreement: 'US-Delaware',
            disputeResolutionClause: 'Binding arbitration through FERP platform'
          }
        },
        status: 'active',
        metadata: {
          projectPhase: 'discovery',
          participants: [expert.metadata?.id, tenant],
          recordingEnabled: true,
          encryptionLevel: 'enterprise',
          platformLockIn: true
        }
      };

      const result = await mutateCreateChannel({
        variables: { tenant, channel: channelInput }
      });

      if (result.data?.createCommunicationChannel) {
        await refetch();
        notifications.show({
          title: 'Communication Channel Created',
          message: `Channel established with ${expert.name} for project communication`,
          color: 'green',
          icon: <IconCheck size={16} />
        });

        return result.data?.createCommunicationChannel ?? null;
      }
    } catch (error) {
      console.error('Failed to create communication channel:', error);
      notifications.show({
        title: 'Channel Creation Failed',
        message: error instanceof Error ? error.message : 'Failed to create communication channel',
        color: 'red',
        icon: <IconAlertTriangle size={16} />
      });
    } finally {
      setLoading(false);
    }
  }, [tenant, mutateCreateChannel, refetch, generateTaskId]);

  // Start a new communication session
  const startSession = useCallback(async (channelId: string, sessionType: string) => {
    setLoading(true);
    try {
      const result = await mutateStartSession({
        variables: { tenant, channelId, sessionType, notes: sessionNotes }
      });

      if (result.data?.startCommunicationSession) {
        await refetch();
        notifications.show({
          title: 'Session Started',
          message: `${sessionType.replace('_', ' ')} session started with expert`,
          color: 'blue',
          icon: <IconVideo size={16} />
        });
        closeModal();
        setSessionNotes('');
      }
    } catch (error) {
      console.error('Failed to start session:', error);
      notifications.show({
        title: 'Session Start Failed',
        message: error instanceof Error ? error.message : 'Failed to start communication session',
        color: 'red'
      });
    } finally {
      setLoading(false);
    }
  }, [tenant, sessionNotes, mutateStartSession, refetch, closeModal]);

  // Calculate total communication costs
  const totalCommunicationCost = useMemo(() => {
    return sessions.reduce((total, session) => total + session.cost, 0);
  }, [sessions]);

  // Calculate platform fees
  const platformFees = useMemo(() => {
    return totalCommunicationCost * 0.20; // 20% platform fee
  }, [totalCommunicationCost]);

  // Get session type icon
  const getSessionTypeIcon = (type: string, size: number = 16) => {
    switch (type) {
      case 'video_call': return <IconVideo size={size} />;
      case 'voice_call': return <IconPhone size={size} />;
      case 'chat_thread': return <IconMessage size={size} />;
      case 'screen_share': return <IconScreenShare size={size} />;
      default: return <IconMessage size={size} />;
    }
  };

  // Get session status color
  const getSessionStatusColor = (status: string) => {
    switch (status) {
      case 'active': return 'green';
      case 'paused': return 'yellow';
      case 'ended': return 'gray';
      case 'archived': return 'dark';
      default: return 'gray';
    }
  };

  // Format duration in minutes to human readable
  const formatDuration = (minutes: number) => {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return hours > 0 ? `${hours}h ${mins}m` : `${mins}m`;
  };

  return (
    <Stack gap="md">
      <Group justify="apart" ta="center">
        <div>
          <Title order={3}>Expert Communication</Title>
          <Text size="sm" color="dimmed">
            Track expert-tenant communication sessions and billing
          </Text>
        </div>
        {!readonly && (
          <Button
            leftSection={<IconVideo size={16} />}
            onClick={openModal}
            variant="filled"
          >
            Start Session
          </Button>
        )}
      </Group>

      {/* Communication Summary */}
      <Group grow>
        <Card withBorder>
          <Group justify="apart" ta="center">
            <div>
              <Text size="xs" color="dimmed">Total Sessions</Text>
              <Text size="lg" fw={600}>{sessions.length}</Text>
            </div>
            <IconMessage size={24} color="blue" />
          </Group>
        </Card>

        <Card withBorder>
          <Group justify="apart" ta="center">
            <div>
              <Text size="xs" color="dimmed">Total Communication Cost</Text>
              <Text size="lg" fw={600}>${totalCommunicationCost.toFixed(2)}</Text>
            </div>
            <IconCurrencyDollar size={24} color="green" />
          </Group>
        </Card>

        <Card withBorder>
          <Group justify="apart" ta="center">
            <div>
              <Text size="xs" color="dimmed">Platform Fees</Text>
              <Text size="lg" fw={600}>${platformFees.toFixed(2)}</Text>
            </div>
            <IconSettings size={24} color="orange" />
          </Group>
        </Card>

        <Card withBorder>
          <Group justify="apart" ta="center">
            <div>
              <Text size="xs" color="dimmed">Active Sessions</Text>
              <Text size="lg" fw={600}>
                {sessions.filter(s => s.status === 'active').length}
              </Text>
            </div>
            <IconClock size={24} color="red" />
          </Group>
        </Card>
      </Group>

      {/* Communication Sessions Table */}
      <Card withBorder>
        <Table>
          <Table.Thead>
            <Table.Tr>
              <Table.Th>Session</Table.Th>
              <Table.Th>Expert</Table.Th>
              <Table.Th>Type</Table.Th>
              <Table.Th>Duration</Table.Th>
              <Table.Th>Cost</Table.Th>
              <Table.Th>Status</Table.Th>
              <Table.Th>Actions</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {sessions.map((session) => (
              <Table.Tr key={session.id}>
                <Table.Td>
                  <div>
                    <Text size="sm" fw={600}>
                      {session.startTime.toLocaleDateString()}
                    </Text>
                    <Text size="xs" color="dimmed">
                      {session.startTime.toLocaleTimeString()}
                    </Text>
                  </div>
                </Table.Td>
                <Table.Td>
                  <Text size="sm">{session.expertName}</Text>
                </Table.Td>
                <Table.Td>
                  <Group gap="xs">
                    {getSessionTypeIcon(session.type)}
                    <Text size="sm" tt="capitalize">
                      {session.type.replace('_', ' ')}
                    </Text>
                  </Group>
                </Table.Td>
                <Table.Td>
                  <div>
                    <Text size="sm">{formatDuration(session.duration)}</Text>
                    <Text size="xs" color="dimmed">
                      {formatDuration(session.billableTime)} billable
                    </Text>
                  </div>
                </Table.Td>
                <Table.Td>
                  <Text size="sm" fw={600}>
                    ${session.cost.toFixed(2)}
                  </Text>
                </Table.Td>
                <Table.Td>
                  <Badge color={getSessionStatusColor(session.status)} variant="light">
                    {session.status}
                  </Badge>
                </Table.Td>
                <Table.Td>
                  <Group gap="xs">
                    <Tooltip label="View session details">
                      <ActionIcon
                        size="sm"
                        variant="light"
                        onClick={() => {
                          // TODO: Open session details modal
                          console.log('View session:', session.id);
                        }}
                      >
                        <IconEye size={14} />
                      </ActionIcon>
                    </Tooltip>
                  </Group>
                </Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>

        {sessions.length === 0 && (
          <Alert color="blue" mt="md">
            <Text size="sm">
              No communication sessions yet. Start a session with an expert to begin tracking.
            </Text>
          </Alert>
        )}
      </Card>

      {/* Start Session Modal */}
      <Modal
        opened={modalOpened}
        onClose={closeModal}
        title="Start Communication Session"
        size="md"
      >
        <Stack gap="md">
          <Alert icon={<IconAlertTriangle size={16} />} color="blue">
            <Text size="sm">
              All communication sessions are automatically recorded and billed.
              Platform fees apply to maintain compliance and audit trails.
            </Text>
          </Alert>

          <TextInput
            label="Session Notes"
            placeholder="Brief description of the session purpose..."
            value={sessionNotes}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSessionNotes(e.target.value)}
          />

          <Group justify="apart" mt="md">
            <Button variant="light" onClick={closeModal}>
              Cancel
            </Button>
            <Button
              onClick={() => startSession('channel-001', newSessionType)}
              loading={loading}
              leftSection={getSessionTypeIcon(newSessionType)}
            >
              Start {newSessionType.replace('_', ' ')}
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Stack>
  );
};

export const createChannelForExpert = async (
  expert: ActorDescriptor,
  integrationId: string,
  tenant: string
): Promise<string | null> => {
  try {
    // This would be implemented with actual GraphQL mutation
    console.log(`Creating communication channel for expert ${expert.name} on integration ${integrationId} for tenant ${tenant}`);

    // Generate backend Snowflake ID for channel (FERP compliance)
    const idRepo = new IdGenerationRepository();
    const channelId = await idRepo.generateId(EntityType.CONTEXT);
    return channelId;
  } catch (error) {
    console.error('Failed to create communication channel:', error);
    return null;
  }
};

/**
 * Create communication context and session task using proper FERP primitives
 * FERP Pattern: Uses Context + Task primitives instead of invalid CommunicationChannel
 */
export async function createExpertCommunicationSession(
  expert: ActorDescriptor,
  integrationId: string,
  tenantId: string,
  sessionType: 'video_call' | 'chat' | 'screen_share' | 'file_transfer' = 'video_call',
  estimatedDuration: number = 60 // minutes
): Promise<{ context: ExpertCommunicationContext; sessionTask: CommunicationSessionTask }> {
  // Generate backend Snowflake IDs (FERP compliance)
  const idRepo = new IdGenerationRepository();
  const contextId = await idRepo.generateId(EntityType.CONTEXT);
  const taskId = await idRepo.generateId(EntityType.workflowNode);

  // Create Context primitive for communication configuration
  const communicationContext: ExpertCommunicationContext = {
    id: contextId, // Backend-generated Snowflake ID
    scope: 'tenant',
    domain_hint: 'expert_communication',
    configuration: {
      expertId: expert.metadata?.id,
      integrationId,
      communicationType: sessionType,
      billing: {
        hourlyRate: (expert.metadata as any)?.hourlyRate || 150,
        platformFeePercentage: 15,
        complianceLevel: 'standard' // TODO: Determine from integration requirements
      },
      enforcement: {
        platformLockIn: true,
        recordingRequired: false,
        auditTrailEnabled: true
      }
    },
    feature_flags: {
      video_enabled: true,
      screen_share_enabled: true,
      file_transfer_enabled: true,
      recording_enabled: false
    }
  };

  // Create Task primitive for communication session
  const sessionTask: CommunicationSessionTask = {
    id: taskId, // Backend-generated Snowflake ID
    entity_ref: `communication-session-${expert.id}`,
    title: `Expert consultation session with ${expert.name}`,
    kind: 'HANDOFF',
    description: `Expert consultation session with ${expert.name}`,
    priority: 'HIGH',
    status: 'OPEN',
    requires_mfa: false,
    assignees: [expert.id],
    watchers: [],
    estimatedEffort: estimatedDuration,
    owner: expert.metadata?.id,
    dueDate: new Date(Date.now() + estimatedDuration * 60 * 1000),
    metadata: {
      sessionType,
      participants: [expert.metadata?.id], // Will add tenant user when session starts
      complianceValidated: true
    }
  };

  return { context: communicationContext, sessionTask };
}